import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, QrCode, CreditCard, User, FileText, CheckCircle, 
  XCircle, Loader2, Clock, Users, RefreshCw, Scan, X,
  Phone, Mail, Calendar, AlertTriangle, DollarSign, Heart, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PaymentDiscountSelector, PaymentStatusBadge } from '../components/PaymentDiscountSelector';

const API = process.env.REACT_APP_BACKEND_URL;

const WaitingRoom = () => {
  const [searchType, setSearchType] = useState('name'); // name, fiscal_code, card_id
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [todayCheckIns, setTodayCheckIns] = useState([]);
  const [loadingCheckIns, setLoadingCheckIns] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [discountTarget, setDiscountTarget] = useState(null); // {check_in_id, base_amount, manual_discount_percent, pro_bono, payment_timing}
  const searchInputRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadTodayCheckIns();
    // Auto-refresh ogni 30 secondi
    const interval = setInterval(loadTodayCheckIns, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadTodayCheckIns = async () => {
    try {
      const res = await axios.get(`${API}/api/waiting-room/today`, { headers });
      setTodayCheckIns(res.data.check_ins || []);
    } catch (err) {
      console.error('Error loading check-ins:', err);
    } finally {
      setLoadingCheckIns(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchValue.trim()) {
      toast.error('Inserisci un valore di ricerca');
      return;
    }

    setSearching(true);
    setSearchResult(null);

    try {
      const res = await axios.post(`${API}/api/waiting-room/search`, {
        search_type: searchType,
        search_value: searchValue.trim()
      }, { headers });

      if (res.data.found) {
        setSearchResult(res.data);
        toast.success('Paziente trovato!');
      } else {
        toast.error(res.data.message || 'Paziente non trovato');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella ricerca');
    } finally {
      setSearching(false);
    }
  };

  const handleCheckIn = async () => {
    if (!searchResult?.patient?.id) return;

    setCheckingIn(true);
    try {
      const appointmentId = searchResult.check_in?.today_appointments?.[0]?.id;
      const response = await axios.post(
        `${API}/api/waiting-room/check-in/${searchResult.patient.id}`,
        null,
        { 
          headers,
          params: { appointment_id: appointmentId }
        }
      );
      
      if (response.data.must_pay) {
        toast.success(
          `Link pagamento inviato a ${searchResult.patient.first_name}! 
           SMS e Email con link €${response.data.payment_amount.toFixed(2)}`,
          { duration: 5000 }
        );
      } else {
        toast.success(`Check-in completato per ${searchResult.patient.first_name} ${searchResult.patient.last_name}!`);
      }
      
      setSearchResult(null);
      setSearchValue('');
      loadTodayCheckIns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel check-in');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCallPatient = async (checkInId, patientName) => {
    const room = prompt(`In quale stanza chiamare ${patientName}?`, 'Stanza A');
    if (!room) return;

    try {
      const response = await axios.post(
        `${API}/api/waiting-room/call-patient/${checkInId}`,
        null,
        { headers, params: { room } }
      );
      
      toast.success(`${patientName} chiamato in ${room}! SMS, Email e Notifica inviati.`);
      loadTodayCheckIns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella chiamata');
    }
  };

  const handleQRScan = (scannedValue) => {
    // Gestisce scansione QR (formato: PRIMEVISION:PV-XXXXXXXX)
    setSearchType('card_id');
    setSearchValue(scannedValue);
    setShowScanner(false);
    // Auto-search
    setTimeout(() => {
      handleSearch();
    }, 100);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const includedServices = [
    "Visita Oculistica Completa",
    "Tonometria",
    "Esame del Fondo Oculare",
    "Pachimetria",
    "Topografia Corneale",
    "OCT Retina",
    "Campo Visivo",
    "Test Lacrimazione",
    "Visita di Controllo"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#daeef9] via-[#f0f7fc] to-[#d4e8f5] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#2A9FCB]">Sala d'Attesa</h1>
          <p className="text-gray-500 mt-1">Ricerca pazienti e gestione check-in</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonna Ricerca */}
          <div className="lg:col-span-2 space-y-6">
            {/* Box Ricerca */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-[#2A9FCB]" />
                Cerca Paziente
              </h2>

              {/* Tipo Ricerca */}
              <div className="flex gap-2 mb-4">
                {[
                  { id: 'name', icon: User, label: 'Nome/Cognome' },
                  { id: 'fiscal_code', icon: FileText, label: 'Codice Fiscale' },
                  { id: 'card_id', icon: CreditCard, label: 'Card ID' }
                ].map(type => (
                  <button
                    key={type.id}
                    onClick={() => { setSearchType(type.id); setSearchResult(null); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      searchType === type.id
                        ? 'bg-[#2A9FCB] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <type.icon className="w-4 h-4" />
                    {type.label}
                  </button>
                ))}
              </div>

              {/* Input Ricerca */}
              <form onSubmit={handleSearch} className="flex gap-3">
                <div className="flex-1 relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={
                      searchType === 'name' ? 'Es. Mario Rossi' :
                      searchType === 'fiscal_code' ? 'Es. RSSMRA80A01H501Z' :
                      'Es. PV-00000001'
                    }
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2A9FCB] focus:ring-2 focus:ring-[#2A9FCB]/20 outline-none text-lg"
                    autoFocus
                  />
                  {searchType === 'card_id' && (
                    <button
                      type="button"
                      onClick={() => setShowScanner(true)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-[#E6F4FA] rounded-lg hover:bg-[#d0ebf5] transition-colors"
                      title="Scansiona QR"
                    >
                      <QrCode className="w-5 h-5 text-[#2A9FCB]" />
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={searching}
                  className="px-6 py-3 bg-[#2A9FCB] text-white rounded-xl font-medium hover:bg-[#2590BA] disabled:opacity-50 flex items-center gap-2"
                >
                  {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                  Cerca
                </button>
              </form>
            </div>

            {/* Risultato Ricerca */}
            {searchResult && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* Header Paziente */}
                <div className={`p-6 ${searchResult.subscription?.has_active ? 'bg-gradient-to-r from-[#2A9FCB] to-[#1E88B5]' : 'bg-gradient-to-r from-gray-500 to-gray-600'} text-white`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm opacity-80 mb-1">
                        {searchResult.patient.is_family_member ? `Membro Famiglia (${searchResult.patient.relationship})` : 'Paziente'}
                      </p>
                      <h3 className="text-2xl font-bold">
                        {searchResult.patient.first_name} {searchResult.patient.last_name}
                      </h3>
                      <p className="text-lg opacity-90 font-mono mt-1">
                        {searchResult.card?.card_number || 'Nessuna Card'}
                      </p>
                    </div>
                    <div className={`px-4 py-2 rounded-xl ${searchResult.subscription?.has_active ? 'bg-white/20' : 'bg-white/10'}`}>
                      {searchResult.subscription?.has_active ? (
                        <div className="text-center">
                          <CheckCircle className="w-8 h-8 mx-auto mb-1" />
                          <p className="text-sm font-medium">ABBONATO</p>
                          <p className="text-xs opacity-80">{searchResult.subscription.plan_name}</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <XCircle className="w-8 h-8 mx-auto mb-1 opacity-80" />
                          <p className="text-sm font-medium">NO ABBONAMENTO</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Dettagli */}
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{searchResult.patient.phone || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="truncate">{searchResult.patient.email || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>{searchResult.patient.date_of_birth || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="font-mono text-xs">{searchResult.patient.fiscal_code || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Box Pagamento */}
                  <div className={`rounded-xl p-4 mb-6 ${
                    searchResult.check_in?.must_pay 
                      ? 'bg-amber-50 border border-amber-200' 
                      : 'bg-green-50 border border-green-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      {searchResult.check_in?.must_pay ? (
                        <>
                          <DollarSign className="w-8 h-8 text-amber-600" />
                          <div>
                            <p className="font-bold text-amber-800">PAGAMENTO RICHIESTO</p>
                            <p className="text-sm text-amber-600">Il paziente non ha abbonamento attivo</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <Heart className="w-8 h-8 text-green-600" />
                          <div>
                            <p className="font-bold text-green-800">PRESTAZIONI INCLUSE</p>
                            <p className="text-sm text-green-600">
                              Piano {searchResult.subscription?.plan_name} - Scade: {searchResult.subscription?.expiry_date?.split('T')[0]}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Lista servizi inclusi */}
                    {!searchResult.check_in?.must_pay && (
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                        {includedServices.map((service, idx) => (
                          <div key={idx} className="flex items-center gap-1 text-xs text-green-700">
                            <CheckCircle className="w-3 h-3" />
                            {service}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Appuntamenti oggi */}
                  {searchResult.check_in?.today_appointments?.length > 0 && (
                    <div className="bg-blue-50 rounded-xl p-4 mb-6">
                      <p className="font-medium text-blue-800 mb-2">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Appuntamenti oggi:
                      </p>
                      {searchResult.check_in.today_appointments.map((apt, idx) => (
                        <div key={idx} className="text-sm text-blue-700">
                          {apt.appointment_time} - {apt.reason || 'Visita'} 
                          {apt.doctor_name && ` con Dr. ${apt.doctor_name}`}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pulsante Check-in */}
                  <button
                    onClick={handleCheckIn}
                    disabled={checkingIn}
                    className="w-full py-4 bg-gradient-to-r from-[#2A9FCB] to-[#1E88B5] text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    {checkingIn ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        Registrazione...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-6 h-6" />
                        EFFETTUA CHECK-IN
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Colonna Check-in di Oggi */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#2A9FCB]" />
                  Check-in Oggi
                </h2>
                <button
                  onClick={loadTodayCheckIns}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Aggiorna"
                >
                  <RefreshCw className={`w-4 h-4 text-gray-500 ${loadingCheckIns ? 'animate-spin' : ''}`} />
                </button>
              </div>

              <div className="text-center py-2 mb-4 bg-[#E6F4FA] rounded-lg">
                <span className="text-2xl font-bold text-[#2A9FCB]">{todayCheckIns.length}</span>
                <span className="text-sm text-gray-500 ml-2">pazienti</span>
              </div>

              {loadingCheckIns ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#2A9FCB]" />
                </div>
              ) : todayCheckIns.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nessun check-in oggi</p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {todayCheckIns.map((ci, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${
                        ci.status === 'waiting_payment' ? 'bg-red-50 border-red-200' :
                        ci.status === 'waiting' ? 'bg-amber-50 border-amber-200' :
                        ci.status === 'called' ? 'bg-blue-50 border-blue-200' :
                        ci.status === 'in_visit' ? 'bg-purple-50 border-purple-200' :
                        'bg-green-50 border-green-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-800">
                            {ci.patient_details?.first_name} {ci.patient_details?.last_name}
                          </p>
                          <p className="text-xs text-gray-500 font-mono">
                            {ci.patient_details?.card_number || ci.card_number}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-600">
                            {formatTime(ci.check_in_time)}
                          </p>
                          {ci.has_subscription ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              {ci.plan_name}
                            </span>
                          ) : (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                              No Abb.
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Status Badge e Azioni */}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            ci.status === 'waiting_payment' ? 'bg-red-100 text-red-700' :
                            ci.status === 'waiting' ? 'bg-amber-100 text-amber-700' :
                            ci.status === 'called' ? 'bg-blue-100 text-blue-700' :
                            ci.status === 'in_visit' ? 'bg-purple-100 text-purple-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {ci.status === 'waiting_payment' && '💳 Attesa Pagamento'}
                            {ci.status === 'waiting' && '🪑 In Attesa'}
                            {ci.status === 'called' && `📢 Chiamato → ${ci.room || 'Stanza'}`}
                            {ci.status === 'in_visit' && '👨‍⚕️ In Visita'}
                            {ci.status === 'completed' && '✅ Completato'}
                          </span>
                          <PaymentStatusBadge
                            proBono={ci.pro_bono || ci.payment_status === 'pro_bono'}
                            paymentStatus={ci.payment_status}
                            amountToPay={ci.amount_to_pay ?? ci.payment_amount}
                            discountPercent={ci.manual_discount_percent}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Sconto/Pro Bono - solo se NON ancora pagato/pro_bono */}
                          {!(ci.pro_bono || ci.payment_status === 'pro_bono' || ci.payment_completed) && (
                            <button
                              onClick={() => setDiscountTarget({
                                check_in_id: ci.id,
                                base_amount: ci.base_amount ?? ci.payment_amount ?? 0,
                                manual_discount_percent: ci.manual_discount_percent || 0,
                                pro_bono: !!ci.pro_bono,
                                payment_timing: ci.payment_timing || 'end',
                                patient_name: `${ci.patient_details?.first_name || ''} ${ci.patient_details?.last_name || ''}`.trim(),
                              })}
                              className="flex items-center gap-1 px-2 py-1 bg-rose-50 text-rose-600 text-xs font-medium rounded-lg hover:bg-rose-100 border border-rose-200 transition-colors"
                              data-testid={`open-discount-modal-${ci.id}`}
                              title="Applica sconto o Pro bono"
                            >
                              <Percent className="w-3 h-3" /> Sconto/Pro bono
                            </button>
                          )}
                          {/* Pulsante Chiama (solo se in attesa e pagamento ok) */}
                          {ci.status === 'waiting' && (
                            <button
                              onClick={() => handleCallPatient(ci.id, `${ci.patient_details?.first_name} ${ci.patient_details?.last_name}`)}
                              className="flex items-center gap-1 px-3 py-1 bg-[#2A9FCB] text-white text-xs font-medium rounded-lg hover:bg-[#2590BA] transition-colors"
                            >
                              <Phone className="w-3 h-3" />
                              Chiama
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Sconto / Pro bono */}
      {discountTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="discount-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 relative">
            <h3 className="text-lg font-bold text-gray-800 mb-1">
              Sconto / Pro bono
            </h3>
            {discountTarget.patient_name && (
              <p className="text-sm text-gray-600 mb-4">Paziente: <strong>{discountTarget.patient_name}</strong></p>
            )}
            <PaymentDiscountSelector
              target="check_in"
              targetId={discountTarget.check_in_id}
              baseAmount={discountTarget.base_amount}
              initialDiscount={discountTarget.manual_discount_percent}
              initialProBono={discountTarget.pro_bono}
              initialTiming={discountTarget.payment_timing}
              onApplied={() => { setDiscountTarget(null); loadTodayCheckIns(); }}
              onClose={() => setDiscountTarget(null)}
            />
          </div>
        </div>
      )}

      {/* Modal Scanner QR (placeholder) */}
      {showScanner && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
            <button 
              onClick={() => setShowScanner(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
            
            <QrCode className="w-16 h-16 mx-auto text-[#2A9FCB] mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">Scansiona QR Card</h3>
            <p className="text-gray-500 mb-6">
              Inquadra il QR code sulla card del paziente
            </p>
            
            {/* Placeholder per camera - in produzione usare react-qr-reader */}
            <div className="bg-gray-100 rounded-xl aspect-square mb-4 flex items-center justify-center">
              <div className="text-center text-gray-400">
                <Scan className="w-12 h-12 mx-auto mb-2" />
                <p className="text-sm">Camera non disponibile</p>
                <p className="text-xs">Inserisci il codice manualmente</p>
              </div>
            </div>

            <div className="flex gap-3">
              <input
                type="text"
                placeholder="PV-00000001"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleQRScan(e.target.value);
                  }
                }}
              />
              <button
                onClick={(e) => {
                  const input = e.target.previousSibling;
                  if (input.value) handleQRScan(input.value);
                }}
                className="px-4 py-2 bg-[#2A9FCB] text-white rounded-lg"
              >
                Cerca
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaitingRoom;
